import { type ReactNode, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SESSION_LOST, signOut } from '../lib/rpc';
import { fetchMe } from '../data/api';
import { usePeople, useRealtimeSync, useSettings } from '../data/queries';
import { buildWorkspace, WorkspaceContext } from '../state/workspace';
import { Icon } from '../components/Icon';
import { LoginPage } from './LoginPage';

/**
 * Sessão → perfil → área. A sessão é um cookie httpOnly da API; /api/auth/session diz quem
 * está logado (null = ninguém). Mostra login, "aguardando aprovação" ou o app.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    retry: 2,
    refetchInterval: q => (q.state.data && !q.state.data.active ? 15_000 : false),
  });
  // Sessão vencida durante o uso: confere de novo quem está logado (sem sessão, mostra o login).
  useEffect(() => {
    const lost = () => void qc.invalidateQueries({ queryKey: ['me'] });
    window.addEventListener(SESSION_LOST, lost);
    return () => window.removeEventListener(SESSION_LOST, lost);
  }, [qc]);

  if (me.isPending) return <Splash />;
  if (me.isError) return <Pending title="Não foi possível carregar seu perfil" text={me.error.message} />;
  if (!me.data) return <LoginPage />;
  if (!me.data.active) {
    return <Pending title="Aguardando aprovação" text={`Sua conta (${me.data.email}) foi criada. Um administrador da área precisa liberar o seu acesso; esta página atualiza sozinha.`} />;
  }
  return <WorkspaceLoader meId={me.data.id}>{children}</WorkspaceLoader>;
}

function WorkspaceLoader({ meId, children }: { meId: string; children: ReactNode }) {
  const people = usePeople();
  const settings = useSettings();
  useRealtimeSync(true);
  const ws = useMemo(() => {
    const me = people.data?.find(p => p.id === meId);
    return me && settings.data ? buildWorkspace(me, people.data!, settings.data) : null;
  }, [people.data, settings.data, meId]);
  if (people.isError || settings.isError) return <Pending title="Não foi possível carregar a área" text={(people.error ?? settings.error)!.message} />;
  if (!ws) return <Splash />;
  return <WorkspaceContext.Provider value={ws}>{children}</WorkspaceContext.Provider>;
}

function Splash() {
  return <div className="auth-page"><div className="auth-brand">{<Icon name="logo" />}<span>Quadro</span></div></div>;
}

function Pending({ title, text }: { title: string; text: string }) {
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand"><Icon name="logo" /><span>Quadro</span></div>
        <h1>{title}</h1>
        <p className="muted">{text}</p>
        <button className="btn" onClick={signOut}>Sair</button>
      </div>
    </div>
  );
}
